import { type FC, useEffect, useRef, useState } from 'react';
import i18next, { t } from 'i18next';

import { useStore } from '@nanostores/react';

import styles from './Animation.module.scss';

import { useNumberLookupAnimationFlag, useUsernameLookupAnimationFlag } from '#store/initAnimation';
import { usernameLookup, numberLookup } from '#store/lookupData';
import DecorTop from '#images/reverse-lookup/animation/top-rotation-img.png';
import DecorBottom from '#images/reverse-lookup/animation/bottom-rotation-img.png';
import Icon1 from '#images/reverse-lookup/animation/animation-1.svg';
import Icon2 from '#images/reverse-lookup/animation/animation-2.svg';
import Icon3 from '#images/reverse-lookup/animation/animation-3.svg';
import Icon4 from '#images/reverse-lookup/animation/animation-4.svg';
import Icon5 from '#images/reverse-lookup/animation/animation-5.svg';
import Icon6 from '#images/reverse-lookup/animation/animation-6.svg';
import Icon7 from '#images/reverse-lookup/animation/animation-7.svg';
import Icon8 from '#images/reverse-lookup/animation/animation-8.svg';
import Icon9 from '#images/reverse-lookup/animation/animation-9.svg';
import SearchLensIcon from '#images/reverse-lookup/animation/search-lens.svg';
import Button from '#components/ui/Button';
import { prepareUrl } from '#utils/prepareUrl';
import { clearFeaturesStorage } from '#utils/clearFeaturesStorage';
import { pageReload } from '#utils/pageReload';

interface Props {
  translationSlug: string;
  animationType: string;
}

const Animation: FC<Props> = ({ translationSlug, animationType }) => {
  const numberLookupActivated = useNumberLookupAnimationFlag();
  const usernameLookupActivated = useUsernameLookupAnimationFlag();
  const $username = useStore(usernameLookup);
  const $phoneNumber = useStore(numberLookup);
  const popupRef = useRef<HTMLDivElement>(null);
  const changer = useRef<number>(1);
  const counter = useRef<number>(0);
  const progressWidth = useRef<number>(0);
  const [showLastStep, setShowLastStep] = useState<boolean>(false);
  const [numbersCounter, setNumbersCounter] = useState<number>(0);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const PROGRESS_LENGTH: number = 18;
  const INTERVAL: number = 200;
  let numbersCount: ReturnType<typeof setInterval> | undefined;
  let dotsInterval: ReturnType<typeof setInterval> | undefined;
  const lang = i18next.language;

  const isActivated = usernameLookupActivated || numberLookupActivated;

  const images = [
    { step: 0, img: Icon1 },
    { step: 2, img: Icon2 },
    { step: 6, img: Icon3 },
    { step: 8, img: Icon4 },
    { step: 10, img: Icon5 },
    { step: 12, img: Icon6 },
    { step: 14, img: Icon7 },
    { step: 17, img: Icon8 },
  ];
  const descriptions = [
    {
      text1: `${translationSlug}.search.progress_1`,
      number: false,
      step: 0,
    },
    {
      text1: `${translationSlug}.search.progress_2_1`,
      text2: `${translationSlug}.search.progress_2_2`,
      number: true,
      step: 2,
    },
    {
      text1: `${translationSlug}.search.progress_3`,
      number: false,
      step: 6,
    },
    {
      text1: `${translationSlug}.search.progress_4`,
      number: false,
      step: 8,
    },
    {
      text1: `${translationSlug}.search.progress_5`,
      number: true,
      step: 10,
    },
    {
      text1: `${translationSlug}.search.progress_6`,
      number: true,
      step: 12,
    },
    {
      text1: `${translationSlug}.search.progress_7`,
      number: true,
      step: 14,
    },
    {
      text1: `${translationSlug}.search.progress_8`,
      number: false,
      step: 17,
    },
  ];

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (isActivated) {
      popupRef.current?.classList.add(styles.active);

      timer = setInterval(() => {
        if (Math.round(counter.current) >= PROGRESS_LENGTH) {
          clearInterval(timer);
          renderReport();
        } else {
          progressWidth.current = (counter.current + 0.3) * 5.7;
          counter.current += 0.3;

          images.forEach(({ step }, index) => {
            if (step === Math.round(counter.current)) {
              if (changer.current !== index) {
                clearInterval(numbersCount);
                setNumbersCounter(0);
              }
              setActiveIndex(index);
              numbersCount = setInterval(() => setNumbersCounter((prev) => prev + 1), 500);
              changer.current = index;
            }
          });
        }
      }, INTERVAL);
    }

    return () => clearInterval(timer);
  }, [isActivated, images.length, INTERVAL]);

  useEffect(() => {
    pageReload();
  }, []);

  const renderReport = () => {
    clearInterval(numbersCount);
    clearInterval(dotsInterval);
    setShowLastStep(true);
    setActiveIndex(-1);
  };

  const handleClick = () => {
    if ($username) {
      clearFeaturesStorage();
      sessionStorage.setItem('isUsernameLookup', 'true');
      sessionStorage.setItem('searchedUsernameLookup', $username);
    } else {
      sessionStorage.setItem('isLookup', 'true');
    }

    window.location.href = prepareUrl('/email', lang);
  };

  if (!isActivated) {
    return null;
  }

  return (
    <>
      <div className={styles.search} ref={popupRef}>
        <div className={styles.wrapper}>
          <img
            className={styles.topRotationImage}
            loading="lazy"
            width="241"
            height="193"
            src={DecorTop.src}
            alt=""
          />
          <img
            className={styles.bottomRotationImage}
            loading="lazy"
            width="404"
            height="338"
            src={DecorBottom.src}
            alt=""
          />
          <div className={styles.iconsContainer}>
            <div className={styles.topIcon}>
              {images.map(({ img, step }, index) => (
                <img
                  key={index}
                  className={`${styles.featureIcon} ${index === activeIndex ? styles.active : ''}`}
                  data-step={step}
                  loading="lazy"
                  width="160"
                  height="160"
                  src={img.src}
                  alt=""
                />
              ))}
            </div>
            <img
              className={`${styles.reportIcon} ${showLastStep ? styles.active : ''}`}
              loading="lazy"
              width="160"
              height="160"
              src={Icon9.src}
              alt=""
            />

            <img
              className={`${styles.searchIcon} ${showLastStep ? styles.hide : ''}`}
              loading="lazy"
              width="77"
              height="77"
              src={SearchLensIcon.src}
              alt=""
            />
          </div>
          <p className={`${styles.reportText} ${showLastStep ? styles.active : ''}`}>
            {t(`${translationSlug}.search.report_text`)}
          </p>
          <div className={`${styles.username} ${showLastStep ? styles.small : ''}`}>
            {animationType?.includes('usernameLookup') && $username}
            {animationType?.includes('numberLookup') && $phoneNumber}
          </div>
          {showLastStep && (
            <Button
              text={t(`${translationSlug}.search.button`)}
              styleType="orange"
              customStyle="lookupFinishedButton"
              eventName="Locate Result"
              onClick={handleClick}
            />
          )}
          <div className={`${styles.progressLine} ${showLastStep ? styles.hide : ''}`}>
            <div
              className={styles.progressContainer}
              style={{ width: `${progressWidth.current || 0}%` }}
            ></div>
          </div>
          <div className={`${styles.bottomText} ${showLastStep ? styles.hide : ''}`}>
            {descriptions.map(({ text1, text2, number, step }, index) => {
              return (
                <p
                  key={step}
                  className={`${styles.searchText} ${index === activeIndex ? styles.active : ''}`}
                  data-step={step}
                >
                  {text1 && t(text1)}
                  {number && <span data-number="true"> {numbersCounter || 1}</span>}
                  {text2 && t(text2)}
                  <span className={styles.searchTextDots}></span>
                </p>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
export default Animation;
